import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion, useAnimation } from "framer-motion";
import { Star, ChevronRight, Quote, MessageSquare, ThumbsUp, ChevronDown, MousePointer, Hand, Fingerprint } from "lucide-react";
import "./theme.css";

gsap.registerPlugin(ScrollTrigger);

const TestimonialsSection = () => {
  const sectionRef = useRef(null);
  const marqueeRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const controls = useAnimation();
  const [isHovering, setIsHovering] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [expandedReviews, setExpandedReviews] = useState({});
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  const GOOGLE_MAPS_LINK = "https://maps.app.goo.gl/mfQxTonXebrLAmw98";

  // Check for mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const sampleReviews = [
    {
      id: 1,
      author_name: "Kousik Bera",
      rating: 5,
      text: "Laminance kitchen did an exceptional job in making our dreams of a unique kind of kitchen in USA. We wanted the European style kitchen and not the traditional shaker style kitchen. He made that possible. Laminance has accommodated all our needs and our dream kitchen speaks for itself. The attention to detail and quality of craftsmanship exceeded our expectations. The team was professional, punctual, and kept the workspace clean throughout the project.",
      relative_time_description: "2 weeks ago",
      profile_photo_url: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face",
      project: "Modern Kitchen Remodel"
    },
    {
      id: 2,
      author_name: "Abhay Kulkarni",
      rating: 5,
      text: "I got introduced to this business while at an event. I found the folks very approachable, not at all pushy or sales men like, rather professional, listening to the folks and working with them per their needs, making suggestions where necessary with proper reasoning for each. I got feedback from other clients that the quality and workmanship is topnotch. Vivek and his team take good care of their clients. I will highly recommend Laminance Cabinetry to anyone looking for quality kitchen solutions.",
      relative_time_description: "1 month ago",
      profile_photo_url: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop&crop=face",
      project: "Contemporary Kitchen"
    },
    {
      id: 3,
      author_name: "Vikalp Chouhan",
      rating: 5,
      text: "Laminance Cabinetry are one of the best in NJ area. Best service, quality and looks. They have very competitive prices and are a must visit shop if you are looking for Kitchen upgrades. Their showroom displays various styles and finishes that helped us visualize our final kitchen design perfectly.",
      relative_time_description: "3 weeks ago",
      profile_photo_url: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face",
      project: "Complete Kitchen Makeover"
    },
  ];

  // Auto-scroll animation
  useEffect(() => {
    let animation;
    
    if (isAutoPlaying && !isDragging && !isHovering && marqueeRef.current && !isMobile) {
      const containerWidth = marqueeRef.current.scrollWidth / 2;
      
      animation = controls.start({
        x: -containerWidth,
        transition: {
          duration: 40,
          ease: "linear",
          repeat: Infinity,
          repeatType: "loop",
        }
      });
    } else {
      controls.stop();
    }

    return () => {
      if (animation) {
        controls.stop();
      }
    };
  }, [isAutoPlaying, isDragging, isHovering, controls, isMobile]);

  // GSAP animations for entrance
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".section-title", {
        opacity: 0,
        y: 30,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".section-title",
          start: "top 85%",
        }
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const toggleReviewExpansion = (reviewId) => {
    setExpandedReviews(prev => ({
      ...prev,
      [reviewId]: !prev[reviewId]
    }));
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {Array.from({ length: 5 }, (_, i) => (
          <Star
            key={i}
            size={isMobile ? 12 : 16}
            className={`${i < rating ? "fill-current text-yellow-400" : "text-gray-300"}`}
          />
        ))}
      </div>
    );
  };

  const TruncatedText = ({ text, reviewId }) => {
    const isExpanded = expandedReviews[reviewId];
    const maxLength = isMobile ? 80 : 150;
    
    if (text.length <= maxLength) {
      return <p className="text-gray-600 leading-relaxed text-sm">"{text}"</p>;
    }

    const truncatedText = isExpanded ? text : `${text.substring(0, maxLength)}...`;

    return (
      <div>
        <p className="text-gray-600 leading-relaxed text-sm">"{truncatedText}"</p>
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleReviewExpansion(reviewId);
          }}
          className="mt-2 flex items-center gap-1 text-xs font-medium transition-colors"
          style={{ color: "var(--color-primary-400)" }}
        >
          <span>{isExpanded ? "Read less" : "Read more"}</span>
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <ChevronDown size={12} />
          </motion.div>
        </button>
      </div>
    );
  };

  // Handle drag/pause controls
  const handleInteractionStart = () => {
    setIsDragging(true);
    setIsAutoPlaying(false);
  };

  const handleInteractionEnd = () => {
    setIsDragging(false);
    setTimeout(() => {
      if (!isHovering) {
        setIsAutoPlaying(true);
      }
    }, 3000);
  };

  // Duplicate reviews for seamless scrolling
  const duplicatedReviews = [...sampleReviews, ...sampleReviews];

  return (
    <section 
      id="Testimonial-section"
      ref={sectionRef}
      className="py-12 md:py-20 relative overflow-hidden"
      style={{
        background: "var(--color-gray-50)",
      }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div 
          className="absolute top-1/4 left-1/4 w-48 md:w-64 h-48 md:h-64 rounded-full"
          style={{ background: "var(--color-primary-400)" }}
        ></div>
        <div 
          className="absolute bottom-1/4 right-1/4 w-48 md:w-64 h-48 md:h-64 rounded-full"
          style={{ background: "var(--color-primary-400)" }}
        ></div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-12">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4"
            style={{
              background: "var(--bg-primary-10)",
              border: "1px solid var(--border-primary-20)",
            }}
          >
            <MessageSquare size={14} style={{ color: "var(--color-primary-400)" }} />
            <span className="text-xs font-medium" style={{ color: "var(--color-primary-400)" }}>
              Client Testimonials
            </span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="section-title text-2xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 px-4"
            style={{
              background: "var(--gradient-primary)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Extremely High Standard of<br className="hidden sm:block" />Customer Satisfaction
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-sm md:text-base max-w-2xl mx-auto px-4"
            style={{ color: "var(--color-gray-600)" }}
          >
            See what our clients have to say about their Laminance kitchen transformations.
          </motion.p>
        </div>

        {/* Testimonials Marquee */}
        <div className="relative mb-12 md:mb-16">
          {/* Controls Info */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="flex flex-wrap justify-center gap-2 mb-4"
          >
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs"
              style={{
                background: "var(--color-white)",
                border: "1px solid var(--border-light)",
              }}
            >
              {isMobile ? <Fingerprint size={12} style={{ color: "var(--color-primary-400)" }} /> : <MousePointer size={12} style={{ color: "var(--color-primary-400)" }} />}
              <span style={{ color: "var(--color-gray-600)" }}>
                {isMobile ? "Swipe to scroll" : "Drag to scroll"}
              </span>
            </div>
            
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs"
              style={{
                background: "var(--color-white)",
                border: "1px solid var(--border-light)",
              }}
            >
              <Hand size={12} style={{ color: "var(--color-primary-400)" }} />
              <span style={{ color: "var(--color-gray-600)" }}>
                {isMobile ? "Tap to pause" : "Hover to pause"}
              </span>
            </div>
          </motion.div>

          {/* Scrollable Container */}
          <div 
            ref={scrollContainerRef}
            className="overflow-x-auto overflow-y-hidden py-2 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
              cursor: isMobile ? 'grab' : 'default',
            }}
            onMouseEnter={() => {
              if (!isMobile) {
                setIsHovering(true);
                setIsAutoPlaying(false);
              }
            }}
            onMouseLeave={() => {
              if (!isMobile) {
                setIsHovering(false);
                setTimeout(() => setIsAutoPlaying(true), 2000);
              }
            }}
            onTouchStart={handleInteractionStart}
            onTouchEnd={handleInteractionEnd}
          >
            <motion.div 
              ref={marqueeRef}
              animate={!isMobile ? controls : {}}
              drag={!isMobile ? "x" : false}
              dragConstraints={scrollContainerRef}
              dragElastic={0.1}
              dragMomentum={true}
              onDragStart={handleInteractionStart}
              onDragEnd={handleInteractionEnd}
              className="flex gap-4 md:gap-6"
              style={{ 
                width: 'max-content',
              }}
            >
              {duplicatedReviews.map((review, index) => {
                const reviewKey = `${review.id}-${index}`;
                const isExpanded = expandedReviews[reviewKey];
                
                return (
                  <motion.div 
                    key={reviewKey} 
                    layout={isExpanded ? "position" : false}
                    className="w-[280px] md:w-80 flex-shrink-0"
                  >
                    <div 
                      className="relative p-4 md:p-5 rounded-xl md:rounded-2xl transition-all duration-300 h-full"
                      style={{
                        background: "var(--color-white)",
                        border: "1px solid var(--border-light)",
                        boxShadow: "var(--shadow-soft)",
                      }}
                    >
                      {/* Rating Badge */}
                      <div className="absolute -top-2 -right-2">
                        <div className="flex items-center gap-1 px-2 py-1 rounded-full"
                          style={{
                            background: "var(--color-white)",
                            border: "1px solid var(--border-light)",
                            boxShadow: "var(--shadow-medium)",
                          }}
                        >
                          {renderStars(review.rating)}
                          <span className="text-xs font-medium ml-0.5" style={{ color: "var(--color-gray-700)" }}>
                            {review.rating}.0
                          </span>
                        </div>
                      </div>

                      {/* Review Content */}
                      <div className="mb-3 md:mb-4">
                        <Quote 
                          size={isMobile ? 18 : 20}
                          className="mb-2 opacity-40"
                          style={{ color: "var(--color-primary-400)" }}
                        />
                        <TruncatedText 
                          text={review.text} 
                          reviewId={reviewKey}
                        />
                      </div>

                      {/* Reviewer Info */}
                      <div className="flex items-center gap-2 pt-3 border-t"
                        style={{ borderColor: "var(--border-light)" }}
                      >
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-full overflow-hidden border-2 flex-shrink-0"
                          style={{ borderColor: "var(--color-white)" }}
                        >
                          <img 
                            src={review.profile_photo_url} 
                            alt={review.author_name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm truncate" style={{ color: "var(--color-gray-600)" }}>
                            {review.author_name}
                          </h4>
                          <p className="text-xs truncate" style={{ color: "var(--color-gray-500)" }}>
                            {review.relative_time_description}
                          </p>
                        </div>
                      </div>

                      {/* Project Info */}
                      {review.project && (
                        <div className="mt-2 text-xs px-2 py-1.5 rounded-lg truncate"
                          style={{
                            background: "var(--bg-primary-10)",
                            color: "var(--color-primary-400)",
                          }}
                        >
                          {review.project}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>

          {/* Gradient Overlays - Desktop only */}
          {!isMobile && (
            <>
              <div className="absolute left-0 top-0 bottom-0 w-16 md:w-24 pointer-events-none"
                style={{
                  background: "linear-gradient(to right, var(--color-gray-50), transparent)",
                }}
              ></div>
              <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 pointer-events-none"
                style={{
                  background: "linear-gradient(to left, var(--color-gray-50), transparent)",
                }}
              ></div>
            </>
          )}
        </div>

        {/* CTA Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center px-4"
        >
          <h3 className="text-xl md:text-2xl font-bold mb-2" style={{ color: "var(--color-gray-600)" }}>
            Ready to Transform Your Kitchen?
          </h3>
          <p className="text-sm text-gray-600 mb-4 md:mb-6">
            Every Laminance kitchen tells a story of trust, precision, and premium craftsmanship.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <motion.a
              href={GOOGLE_MAPS_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 md:px-6 py-2.5 md:py-3 rounded-lg font-semibold flex items-center justify-center gap-2 text-sm"
              style={{
                background: "var(--color-primary-400)",
                color: "var(--color-white)",
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <ThumbsUp size={16} />
              <span>Write a Review</span>
              <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </motion.a>
            
            <motion.a
              href={GOOGLE_MAPS_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 md:px-6 py-2.5 md:py-3 rounded-lg font-semibold flex items-center justify-center gap-2 text-sm"
              style={{
                background: "var(--color-white)",
                border: "2px solid var(--color-primary-400)",
                color: "var(--color-primary-400)",
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>View All Reviews</span>
            </motion.a>
          </div>

          <p className="mt-4 text-xs" style={{ color: "var(--color-gray-500)" }}>
            All reviews are verified from Google Business Profile
          </p>
        </motion.div>
      </div>

      {/* CSS for hiding scrollbar */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        @media (max-width: 768px) {
          .overflow-x-auto {
            -webkit-overflow-scrolling: touch;
            scroll-snap-type: x mandatory;
          }
          
          .overflow-x-auto > div {
            scroll-snap-align: start;
          }
        }
      `}</style>
    </section>
  );
};

export default TestimonialsSection;